import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';

import { selectLanguage } from '@ninety/platform/state';
import { TerraIconModule } from '@ninety/terra';
import { VtoLayoutModeState } from '@ninety/web/pages/vto/models/vto-layout-mode-state.enum';
import { LegacyVTOGridLayoutActions } from '@ninety/web/pages/vto/services/legacy-vto-grid-layout-actions.service';

/** @deprecated DEV-14518 Legacy global dashboard is deprecated. Replacement under development. See ticket. */
@Component({
  selector: 'ninety-grid-layout-toolbar-buttons',
  styleUrls: ['./grid-layout-toolbar-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if ((gridLayoutActions.isSingleColumnView$ | async) === false) {
      @if (!layoutModeActive) {
        <button
          id="edit-layout-button"
          mat-button
          class="center-content"
          matTooltipClass="ninety-tooltip"
          matTooltip="Enables drag and drop as well as resizing of individual sections in the {{
            (language$ | async)?.vto?.route
          }}."
          [disabled]="layoutModeDisabled"
          (click)="gridLayoutActions.emitLayoutModeStateChange(LayoutModeState.REQUEST_ENABLE)">
          <terra-icon class="left-icon" icon="edit" />
          Edit Layout
        </button>
      } @else {
        <button
          mat-button
          id="reset-layout-button"
          class="center-content"
          matTooltip="Undo layout changes and exit"
          matTooltipClass="ninety-tooltip"
          (click)="gridLayoutActions.emitResetLayout()"
          [disabled]="layoutModeDisabled">
          <terra-icon class="left-icon" icon="undo" />
          Cancel
        </button>
        <button
          data-cy="grid-layout-toolbar-buttons_manage-sections-button"
          mat-button
          id="manage-sections-button"
          class="center-content"
          matTooltip="Add, hide, or remove sections from the layout"
          matTooltipClass="ninety-tooltip"
          (click)="gridLayoutActions.emitLaunchManageSections()"
          [disabled]="layoutModeDisabled">
          <terra-icon class="left-icon" icon="settings" />
          Manage Sections
        </button>
        <button
          data-cy="grid-layout-toolbar-buttons_save-and-exit-button"
          mat-raised-button
          id="save-layout-button"
          class="center-content"
          matTooltip="Save layout changes and exit"
          matTooltipClass="ninety-tooltip"
          (click)="gridLayoutActions.emitPersistLayoutChanges()"
          [disabled]="layoutModeDisabled"
          [class.white-background]="!fillSaveButtonWithColor"
          [color]="fillSaveButtonWithColor ? 'accent' : null">
          <terra-icon class="left-icon" icon="save" />
          Save and Exit
        </button>
      }
    }
  `,
  standalone: true,
  imports: [MatButton, MatTooltip, TerraIconModule, AsyncPipe],
})
export class GridLayoutToolbarButtonsComponent {
  gridLayoutActions = inject(LegacyVTOGridLayoutActions);
  private readonly store = inject(Store);

  readonly LayoutModeState = VtoLayoutModeState;

  /**
   * When true, shows the save, reset, and manage sections buttons.
   * When false, shows button to enable layout mode
   */
  @Input() layoutModeActive = false;

  /**
   * When true, layout mode button is shown, but it is disabled.
   * Prevent edits to the layout mode when VTO is loading.
   */
  @Input() layoutModeDisabled = false;

  /**
   * This component is used in places with backgrounds that are white and the accent color. This property can be used
   * to style the save button based on the background color of the parent component.
   */
  @Input() fillSaveButtonWithColor = false;

  language$ = this.store.select(selectLanguage);
}
